import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import Loading from 'designs/Loading'
import _ from 'lodash'
import { useRouter } from 'next/router'
import qs from 'qs'
import { useCallback } from 'react'
import { useAuth } from 'shared/contexts/Auth'

import { useKDCApplyMutation } from '../queries/useKDCApplyMutation'
import { useKDCAttrQuery } from '../queries/useKDCAttrQuery'
import { fetchMyKDCApply } from '../queries/useMyKDCApplyQuery'
import { IKdcAttr } from '../types'

dayjs.extend(isBetween)

const useKDCApply = (courseId: string) => {
  const router = useRouter()
  const { profile, isSigned, signInUrl } = useAuth()
  const prevLocation = (router.query.prevLocation as string) || '/'
  const kdcApply = useKDCApplyMutation()
  const { data: kdcAttr, isLoading: isKdcAttrLoading } = useKDCAttrQuery(courseId)

  const checkSignIn = useCallback(() => {
    const state = qs.stringify({ state: location.pathname })
    const signInUrlWithState = `${signInUrl}&${state}&register_from=${prevLocation}`
    window.location.replace(signInUrlWithState)

    return <Loading />
  }, [prevLocation, signInUrl])

  const submitApply = useCallback(
    (data: IKdcAttr) => {
      const gclid = JSON.parse(localStorage.getItem('gclid') as string)
      kdcApply.mutate(
        {
          courseId: data?.courseId,
          kdcAttrId: data?.id,
          userId: profile?.id,
          ...(!_.isEmpty(gclid) && {
            gclid: gclid.gclid,
          }),
        },
        {
          onSuccess: () => {
            if (!profile?.isCertificated) {
              void router.push(`/onboarding/${data.courseId}/apply/identity-verification`)
            } else {
              void router.push(`/onboarding/${data.courseId}/apply/card`)
            }
          },
        },
      )
    },
    [kdcApply, profile, router],
  )

  const onApply = useCallback(
    async (curId: string, onDialogOpen?: () => void) => {
      if (!isSigned) {
        checkSignIn()
      } else {
        if (!_.isEmpty(kdcAttr) && kdcAttr) {
          const myApply = await fetchMyKDCApply(kdcAttr[0]?.id)
          if (_.isEmpty(myApply)) {
            submitApply(kdcAttr[0])
          } else {
            if (!profile?.isCertificated) {
              void router.push(`/onboarding/${curId}/apply/identity-verification`)
            } else if (
              profile?.isCertificated &&
              (_.isEmpty(myApply[0]?.hrdInfo) || _.isEmpty(myApply[0]?.hrdInfo.haveCardAt))
            ) {
              void router.push(`/onboarding/${curId}/apply/card`)
            } else if (
              !_.isEmpty(myApply[0]?.hrdInfo.haveCardAt) &&
              _.isEmpty(myApply[0]?.passedAt)
            ) {
              void router.push(`/onboarding/${curId}/apply/enrollment`)
            } else if (!_.isEmpty(myApply[0]?.passedAt) && _.isEmpty(myApply[0]?.paidAt)) {
              void router.push(
                {
                  pathname: `/onboarding/${curId}/apply/payment`,
                  query: { schoolId: curId, startedAt: kdcAttr[0].startedAt },
                },
                `/onboarding/${curId}/apply/payment`,
              )
            } else if (!_.isEmpty(myApply[0]?.paidAt)) {
              if (onDialogOpen) {
                void onDialogOpen()
              }
            }
          }
        }
      }
    },
    [checkSignIn, isSigned, kdcAttr, profile, router],
  )

  return {
    onApply,
    isKdcAttrLoading,
    kdcAttr,
    checkSignIn,
    isSigned,
    profile,
  }
}

export default useKDCApply
